import type { ComponentConfig } from "@puckeditor/core";
import { ContentBlock } from './content-block';
import { ContentBlockFields } from "./content-block.fields";
import type { ContentBlockProps } from './content-block';
import { ImagePicker } from "../editor/ImagePicker";

/** Settings that only act on an image. */
const IMAGE_ONLY_FIELDS = ["imageAlt", "imageStyle", "imageColor", "imageSize"] as const;

const ContentBlockConfig: ComponentConfig<ContentBlockProps> = {
  defaultProps: {
    backgroundColor: "white",
    content:
      "Add your content here. You can include multiple paragraphs by separating them with double line breaks.\n\nThis is a second paragraph to demonstrate the formatting.",
    heading: "Your Heading Here",
    headingSize: "large",
    imageAlt: "",
    imageColor: "original",
    imageStyle: "shadow",
    layout: "image-left",
    variant: "standard",
  },
  fields: {
    heading: {
      ...ContentBlockFields.heading,
      contentEditable: true,
    },
    content: {
      ...ContentBlockFields.content,
      contentEditable: true,
    },
    imageUrl: {
      ...ContentBlockFields.imageUrl,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Image" />,
    },
    imageAlt: ContentBlockFields.imageAlt,
    layout: ContentBlockFields.layout,
    imageStyle: ContentBlockFields.imageStyle,
    imageColor: ContentBlockFields.imageColor,
    imageSize: ContentBlockFields.imageSize,
    backgroundColor: ContentBlockFields.backgroundColor,
    headingSize: ContentBlockFields.headingSize,
    variant: ContentBlockFields.variant,
  },
  label: "Content Block",
  // Spread rather than list props: an enumerated signature silently drops any
  // Field added later (imageStyle did exactly that, so the image always kept
  // The "shadow" default).
  render: ({ id, puck: _puck, editMode: _editMode, ...props }) => <ContentBlock id={id} {...props} />,
  /**
   * Nothing reads the image in the text-only layout, and with no image set the three
   * image layouts render the same thing as each other.
   */
  resolveFields: (data, { fields }) => {
    const props = (data.props || {}) as { layout?: string; imageUrl?: string; variant?: string },
      visible = { ...(fields as Record<string, unknown>) },
      hasImage = Boolean(props.imageUrl?.trim());

    if (props.layout === "no-image" || props.layout === "statement" || !hasImage) {
      for (const name of IMAGE_ONLY_FIELDS) {
        Reflect.deleteProperty(visible, name);
      }
    }

    if (props.layout === "no-image" || props.layout === "statement") {
      delete visible.imageUrl;
    }

    // The statement heading is the display element; its size is the layout.
    // Bold sets its heading's size itself.
    if (props.layout === "statement" || props.variant === "bold") {
      delete visible.headingSize;
    }

    return visible as typeof fields;
  },
};

export { ContentBlockConfig };
